import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Clock,
  Shield,
  ShieldAlert,
  Eye,
  Loader2,
  Save,
  Search,
  CheckCircle2,
  AlertCircle,
  X,
  ClipboardCheck,
  ArrowRightLeft,
  ArrowUpRight,
  Zap,
  Filter,
  Ban,
  PauseCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { CollapsibleCardHeader } from '@/components/ui/collapsible-card-header';
import { NoteLine } from '@/components/shared/NoteLine';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useLocation } from '@/contexts/LocationContext';
import { useOrganizationMutations } from '@/features/organization/hooks/useOrganizationMutations';
import {
  resolveInventoryPushSettings,
  resolveNotificationSettings,
  type InventoryPushSettings,
  type NotificationSettings,
} from '@/features/organization/components/settingsShared';
import {
  DEFAULT_ALWAYS_ZERO_LIMIT,
  MAX_ALWAYS_ZERO_LIMIT,
  type OrganizationSettings as SharedOrganizationSettings,
} from '@threadheads/shared';
import { ErpStockAuthoritySection } from './ErpStockAuthoritySection';
import { PushSkuRulesSection } from './PushSkuRulesSection';
import { ALWAYS_ZERO_BADGE_CLASS } from './push-rule-styles';
import { pushRunState } from '../utils/push-sku-rules';

type FirstPushAuditResult = Awaited<ReturnType<ReturnType<typeof trpc.useUtils>['organization']['auditFirstPush']['fetch']>>;

const INTERVAL_OPTIONS = [
  { value: 5, label: 'Every 5 minutes' },
  { value: 10, label: 'Every 10 minutes' },
  { value: 15, label: 'Every 15 minutes' },
  { value: 30, label: 'Every 30 minutes' },
  { value: 60, label: 'Every hour' },
  { value: 240, label: 'Every 4 hours' },
  { value: 720, label: 'Every 12 hours' },
  { value: 1440, label: 'Every 24 hours' },
];

export function StockSyncSettings() {
  const { t } = useTranslation('inventory');
  const { organization } = useOrganization();
  const { updateOrganization } = useOrganizationMutations();
  const utils = trpc.useUtils();

  const [settings, setSettings] = useState<InventoryPushSettings>(() =>
    resolveInventoryPushSettings(organization?.settings as SharedOrganizationSettings | null),
  );
  const [notifSettings, setNotifSettings] = useState<NotificationSettings>(() =>
    resolveNotificationSettings(organization?.settings as SharedOrganizationSettings | null),
  );
  const [hasChanges, setHasChanges] = useState(false);
  const [dismissedStatus, setDismissedStatus] = useState(false);
  const [openSections, setOpenSections] = useState<Set<string>>(new Set(['push']));
  const toggleSection = (key: string) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    const orgSettings = organization?.settings as SharedOrganizationSettings | null;
    setSettings(resolveInventoryPushSettings(orgSettings));
    setNotifSettings(resolveNotificationSettings(orgSettings));
    setHasChanges(false);
  }, [organization]);

  const update = (partial: Partial<InventoryPushSettings>) => {
    setSettings((prev) => ({ ...prev, ...partial }));
    setHasChanges(true);
  };

  // Notification settings now live on /notifications, but we still ship
  // the notifSettings state through this form's save so that toggling
  // unrelated stock-sync controls doesn't reset the notifications JSON.

  // --- BLU-343: Exclusion list state ---
  const { locations } = useLocation();

  // BLU-354c: read the hybrid location set to show a live count on the pointer card.
  const hybridIdsQuery = trpc.productionSettings.getOrgHybridLocationIds.useQuery(undefined, {
    staleTime: 15_000,
  });
  const hybridCount = hybridIdsQuery.data?.hybridLocationIds.length ?? 0;

  // BLU-1541: Skip / Always 0 SKUs live in their own column, applied at once
  // by PushSkuRulesSection (never through this form's save). The header badge
  // reads the same capped list the section shows (shared query cache); it
  // loads with this tab, so a collapsed card still says SKUs are being zeroed.
  const pushRulesQuery = trpc.stockSync.listPushSkuRules.useQuery(undefined, { staleTime: 30_000 });
  const ruleCounts: { skip: number; alwaysZero: number } | undefined = pushRulesQuery.data?.counts;

  // Whether the rules can take effect yet reads the SAVED settings, never the
  // unsaved edits in this form: the push runs on what is stored.
  const savedPush = useMemo(
    () => resolveInventoryPushSettings(organization?.settings as SharedOrganizationSettings | null),
    [organization],
  );
  const runState = pushRunState(savedPush);

  const toggleLocationExclusion = useCallback((locationId: string) => {
    const current = settings.excludedLocationIds ?? [];
    if (current.includes(locationId)) {
      update({ excludedLocationIds: current.filter(id => id !== locationId) });
    } else {
      update({ excludedLocationIds: [...current, locationId] });
    }
  }, [settings.excludedLocationIds]);

  const excludedLocationSet = useMemo(() => new Set(settings.excludedLocationIds ?? []), [settings.excludedLocationIds]);

  const skippedLocationCount = settings.excludedLocationIds?.length ?? 0;

  // --- Discovery status polling ---
  const statusQuery = trpc.organization.getPodDiscoveryStatus.useQuery(undefined, {
    refetchInterval: (query) => {
      return query.state.data?.status === 'running' ? 2_000 : false;
    },
  });

  const discoveryStatus = statusQuery.data;
  const isRunning = discoveryStatus?.status === 'running';
  const isComplete = discoveryStatus?.status === 'complete';
  const isError = discoveryStatus?.status === 'error';
  const showStatus = (isRunning || ((isComplete || isError) && !dismissedStatus));

  // Auto-dismiss complete status after 60s
  useEffect(() => {
    if (isComplete) {
      setDismissedStatus(false);
      const timer = setTimeout(() => setDismissedStatus(true), 60_000);
      return () => clearTimeout(timer);
    }
    if (isRunning) {
      setDismissedStatus(false);
    }
    return undefined;
  }, [isComplete, isRunning]);

  const discoveryMutation = trpc.organization.triggerPodDiscovery.useMutation({
    onSuccess: (result) => {
      if (result.alreadyRunning) {
        toast.info('Discovery is already running');
      } else if (result.started) {
        // Fire-and-forget — polling will show progress
        setDismissedStatus(false);
        statusQuery.refetch();
      } else {
        toast.error('Could not start discovery: check that inventory push is enabled');
      }
    },
    onError: (err) => toast.error(err.message || 'Discovery failed'),
  });

  // --- First Sync Audit ---
  const [isAuditing, setIsAuditing] = useState(false);
  const [auditResult, setAuditResult] = useState<FirstPushAuditResult | null>(null);

  const runFirstPushAudit = async () => {
    setIsAuditing(true);
    try {
      const result = await utils.organization.auditFirstPush.fetch();
      setAuditResult(result);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Audit failed, please try again');
    } finally {
      setIsAuditing(false);
    }
  };

  // Slack test moved to /notifications.

  const handleReset = () => {
    const orgSettings = organization?.settings as SharedOrganizationSettings | null;
    setSettings(resolveInventoryPushSettings(orgSettings));
    setNotifSettings(resolveNotificationSettings(orgSettings));
    setHasChanges(false);
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    const orgSettings = (organization?.settings ?? {}) as unknown as SharedOrganizationSettings;
    updateOrganization.mutate({
      settings: {
        ...orgSettings,
        inventoryPush: { ...settings, notifications: notifSettings },
      },
    });
    // hasChanges is reset by the useEffect on [organization] when the mutation succeeds
    // and reloadOrganization fires — not here, so the save bar stays visible on error
  };

  const progressPercent = discoveryStatus?.totalSkus
    ? Math.round((discoveryStatus.processedSkus / discoveryStatus.totalSkus) * 100)
    : 0;

  // BLU-553 delta block — cast-gated until settingsShared/@threadheads/shared
  // add `deltaBlockEnabled` + `deltaBlockThreshold` to InventoryPushSettings
  // (org-settings change lands with the parallel backend PR).
  const deltaBlock = settings as InventoryPushSettings & {
    deltaBlockEnabled?: boolean;
    deltaBlockThreshold?: number;
  };
  // Default TRUE — must match the worker (?? true) and the Zod default,
  // or an unsaved org shows the guard 'off' while it actively blocks.
  const deltaBlockEnabled = deltaBlock.deltaBlockEnabled ?? true;
  const deltaBlockThreshold = deltaBlock.deltaBlockThreshold ?? 5000;
  const alwaysZeroNoLimit = settings.alwaysZeroLimit === 0;

  return (
    <form onSubmit={handleSave} className="space-y-6">
      {/* Push Configuration */}
      <Collapsible open={openSections.has('push')} onOpenChange={() => toggleSection('push')}>
      <Card>
        <CollapsibleCardHeader
          open={openSections.has('push')}
          icon={
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15">
              <Zap className="h-5 w-5 text-primary" aria-hidden="true" />
            </div>
          }
          title="Push Configuration"
          description="Control how PrintRun stock quantities are pushed to Shopify."
        />
        <CollapsibleContent>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="push-enabled">Enable Inventory Push</Label>
              <p className="text-sm text-muted-foreground">Turn on to allow manual and single-SKU pushes to Shopify. Does not start automatic sync.</p>
            </div>
            <Switch id="push-enabled" checked={settings.enabled} onCheckedChange={(checked) => update({ enabled: checked })} />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <Label htmlFor="auto-sync">Auto Sync</Label>
                {settings.autoSyncEnabled && settings.enabled && (
                  <Badge variant="outline" className="text-green-600 border-green-300">Active</Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">Automatically push changed stock to Shopify on a recurring schedule.</p>
            </div>
            <Switch id="auto-sync" checked={settings.autoSyncEnabled} onCheckedChange={(checked) => update({ autoSyncEnabled: checked })} disabled={!settings.enabled} />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <Label htmlFor="dry-run">Dry Run Mode</Label>
                {settings.dryRunMode && (
                  <Badge variant="outline" className="text-amber-600 border-amber-300">
                    <Eye className="h-3 w-3 mr-1" />Active
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">Log what would be pushed without calling Shopify.</p>
            </div>
            <Switch id="dry-run" checked={settings.dryRunMode} onCheckedChange={(checked) => update({ dryRunMode: checked })} />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="seed-inv">Seed Inventory from Shopify</Label>
              <p className="text-sm text-muted-foreground">Pull Shopify on-hand quantities as initial stock for new pre-made SKUs. Disable if PrintRun is source of truth.</p>
            </div>
            <Switch id="seed-inv" checked={settings.seedInventory} onCheckedChange={(checked) => update({ seedInventory: checked })} />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <Label htmlFor="auto-stock">Auto-Stock at Location</Label>
                {settings.autoStockAtLocation && settings.enabled && (
                  <Badge variant="outline" className="text-green-600 border-green-300">Active</Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                During push, automatically enable inventory tracking on untracked variants and activate products at
                Shopify locations where they are not yet stocked. When off, untracked and unstocked products are skipped.
              </p>
            </div>
            <Switch id="auto-stock" checked={settings.autoStockAtLocation} onCheckedChange={(checked) => update({ autoStockAtLocation: checked })} disabled={!settings.enabled} />
          </div>

          <div className="border-t pt-4 space-y-4">
            <div className="space-y-0.5">
              <Label>{t('stockSync.settings.productEligibility.title')}</Label>
              <p className="text-sm text-muted-foreground">
                {t('stockSync.settings.productEligibility.description')}
              </p>
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <Label htmlFor="push-approved-drafts">
                  {t('stockSync.settings.pushApprovedDrafts.label')}
                </Label>
                <p id="push-approved-drafts-help" className="text-sm text-muted-foreground">
                  {t('stockSync.settings.pushApprovedDrafts.help')}
                </p>
              </div>
              <Switch
                id="push-approved-drafts"
                aria-describedby="push-approved-drafts-help"
                checked={settings.pushApprovedDraftProducts}
                onCheckedChange={(checked) => update({ pushApprovedDraftProducts: checked })}
                disabled={!settings.enabled}
              />
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <Label htmlFor="discover-newly-active">
                  {t('stockSync.settings.discoverNewlyActive.label')}
                </Label>
                <p id="discover-newly-active-help" className="text-sm text-muted-foreground">
                  {t('stockSync.settings.discoverNewlyActive.help')}
                </p>
              </div>
              <Switch
                id="discover-newly-active"
                aria-describedby="discover-newly-active-help"
                checked={settings.autoDiscoverNewActiveProducts}
                onCheckedChange={(checked) => update({ autoDiscoverNewActiveProducts: checked })}
                disabled={!settings.enabled}
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <Label htmlFor="enforce-deny">Stop Selling at Zero Stock</Label>
                {settings.enforceDenyPolicy && settings.enabled && (
                  <Badge variant="outline" className="text-green-600 border-green-300">Active</Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                During a live push, set newly discovered variants still marked &ldquo;Continue selling when out of
                stock&rdquo; to deny, so a pushed quantity of 0 actually makes them unsellable. Has no effect under Dry
                Run Mode. To flip the existing backlog, run the{' '}
                <code className="rounded bg-background px-1 py-0.5 text-xs">npm run audit:shopify-inventory-policy</code>{' '}
                script once.
              </p>
            </div>
            <Switch id="enforce-deny" checked={settings.enforceDenyPolicy} onCheckedChange={(checked) => update({ enforceDenyPolicy: checked })} disabled={!settings.enabled} />
          </div>

          <Link
            to="/production/settings#hybrid-fulfillment"
            className="group block rounded-md border bg-muted/30 p-3 space-y-1.5 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
          >
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Pre-Print Hybrid Mode</span>
              {hybridCount > 0 && (
                <Badge variant="outline" className="text-emerald-700 border-emerald-300 tabular-nums">
                  Active at {hybridCount} location{hybridCount === 1 ? '' : 's'}
                </Badge>
              )}
              {hybridIdsQuery.isSuccess && hybridCount === 0 && (
                <Badge variant="outline" className="text-muted-foreground">No pre-printed stock yet</Badge>
              )}
              <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground ml-auto group-hover:text-foreground transition-colors" />
            </div>
            <p className="text-sm text-muted-foreground">
              Shopify shows the blank and pre-print combined wherever pre-printed stock exists; products
              fall back to printing the blank automatically when it runs out. There is nothing to configure,
              this happens at every location.
            </p>
          </Link>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <Label>Push Interval</Label>
            </div>
            <div className="flex items-center gap-3">
              <Select value={String(settings.intervalMinutes)} onValueChange={(v) => update({ intervalMinutes: Number(v) })}>
                <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INTERVAL_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">how often stock is pushed to Shopify</span>
            </div>
          </div>

          {/* BLU-677: Push on events — accelerate the scheduled push when a
              stock-increasing event fires, so Shopify sees new stock sooner
              than the next cycle. All off by default. */}
          <div className="border-t pt-4 space-y-4">
            <div className="space-y-0.5">
              <Label>Push on events</Label>
              <p className="text-sm text-muted-foreground">
                Push affected SKUs to Shopify the moment stock increases from one of these
                events, rather than waiting for the next scheduled push. Each is an acceleration
                of the scheduled push, off by default.
              </p>
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <Label htmlFor="push-on-po-receive">Purchase order received</Label>
                <p className="text-sm text-muted-foreground">
                  Push the received SKUs as soon as a purchase order is marked received.
                </p>
              </div>
              <Switch
                id="push-on-po-receive"
                checked={settings.pushOnPurchaseOrderReceive}
                onCheckedChange={(c) => update({ pushOnPurchaseOrderReceive: c })}
                disabled={!settings.enabled || !settings.autoSyncEnabled}
              />
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <Label htmlFor="push-on-transfer-receive">Transfer received</Label>
                <p className="text-sm text-muted-foreground">
                  Push the destination location&rsquo;s SKUs when a stock transfer is received.
                </p>
              </div>
              <Switch
                id="push-on-transfer-receive"
                checked={settings.pushOnTransferReceive}
                onCheckedChange={(c) => update({ pushOnTransferReceive: c })}
                disabled={!settings.enabled || !settings.autoSyncEnabled}
              />
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <Label htmlFor="push-on-stock-adjustment">Stock adjustment</Label>
                <p className="text-sm text-muted-foreground">
                  Push a SKU immediately when its on-hand quantity is manually adjusted, up or
                  down, so a correction reaches Shopify right away.
                </p>
              </div>
              <Switch
                id="push-on-stock-adjustment"
                checked={settings.pushOnStockAdjustment}
                onCheckedChange={(c) => update({ pushOnStockAdjustment: c })}
                disabled={!settings.enabled || !settings.autoSyncEnabled}
              />
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <Label htmlFor="push-on-stocktake">Stocktake committed (coming soon)</Label>
                <p className="text-sm text-muted-foreground">
                  Coming soon. Will push every SKU changed by a committed stocktake. Because a
                  stocktake can touch many SKUs at once, this needs batched handling and isn&rsquo;t
                  wired up yet. Those SKUs still sync on the next scheduled push.
                </p>
              </div>
              <Switch
                id="push-on-stocktake"
                checked={settings.pushOnStocktake}
                onCheckedChange={(c) => update({ pushOnStocktake: c })}
                disabled
              />
            </div>
          </div>

          {/* BLU-495 Q4: safety pipeline explainer — replaces the deleted Back-Sync card's explainer */}
          <div className="rounded-md border border-muted bg-muted/30 p-3 text-sm text-muted-foreground">
            When a SKU&rsquo;s stock minus the safety buffer reaches zero, Shopify
            receives <span className="font-mono">qty=0</span>, so the variant becomes
            unsellable on the storefront until stock returns. The Mass Out-of-Stock
            Guard below halts the entire push when too many SKUs cross to zero in
            one cycle.
          </div>

          {/* These two numbers are the LAST resort, not the setting. Naming
              the resolution order in plain words is what stops an operator
              tuning them and wondering why nothing moved.
              Split across prefix / link / suffix keys rather than one string
              with markup: a translator has to be able to put the link where
              the sentence needs it, and zh puts it in a different place. */}
          <div className="space-y-1 text-xs text-muted-foreground">
            <p>{t('stockSync.settings.holdBackOrder.intro')}</p>
            <ol className="ml-4 list-decimal space-y-0.5">
              <li>{t('stockSync.settings.holdBackOrder.step1')}</li>
              <li>
                {t('stockSync.settings.holdBackOrder.step2Prefix')}{' '}
                {/* Router Link, not <a>: a bare href full-reloads the SPA, dropping
                    every unsaved edit on this settings page. */}
                <Link to="/stock-hold-back" className="underline decoration-dotted">
                  {t('stockSync.settings.holdBackOrder.step2Link')}
                </Link>{' '}
                {t('stockSync.settings.holdBackOrder.step2Suffix')}
              </li>
              <li>{t('stockSync.settings.holdBackOrder.step3')}</li>
            </ol>
          </div>

          {/* Org fallback buffer (absolute) — unit chip + a11y wired */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <Label htmlFor="safety-buffer-units">
                {t('stockSync.settings.orgFallbackUnits.label')}
              </Label>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <div className="relative w-28">
                <Input
                  id="safety-buffer-units"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={100}
                  aria-describedby="safety-buffer-units-help"
                  value={settings.safetyBuffer}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val) && val >= 0 && val <= 100) update({ safetyBuffer: val });
                  }}
                  className="pr-12 tabular-nums"
                />
                <span className="absolute inset-y-0 right-0 grid place-items-center px-3 text-xs text-muted-foreground border-l pointer-events-none">
                  units
                </span>
              </div>
              <span id="safety-buffer-units-help" className="text-sm text-muted-foreground">
                {t('stockSync.settings.orgFallbackUnits.help')}
              </span>
            </div>
          </div>

          {/* BLU-495 Q4: Safety Buffer (Percent) — sibling, no icon (icon discipline) */}
          <div className="space-y-2">
            <Label htmlFor="safety-buffer-percent" className="pl-6">
              {t('stockSync.settings.orgFallbackPercent.label')}
            </Label>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-6">
              <div className="relative w-28">
                <Input
                  id="safety-buffer-percent"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={100}
                  aria-describedby="safety-buffer-percent-help"
                  value={settings.safetyBufferPercent ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      update({ safetyBufferPercent: null });
                      return;
                    }
                    const val = parseInt(raw, 10);
                    if (!isNaN(val) && val >= 0 && val <= 100) {
                      update({ safetyBufferPercent: val });
                    }
                  }}
                  className="pr-10 tabular-nums"
                />
                <span className="absolute inset-y-0 right-0 grid place-items-center px-3 text-xs text-muted-foreground border-l pointer-events-none">
                  %
                </span>
              </div>
              {(settings.safetyBufferPercent == null || settings.safetyBufferPercent === 0) && (
                <Badge variant="outline" className="text-muted-foreground">Off</Badge>
              )}
              <span id="safety-buffer-percent-help" className="text-sm text-muted-foreground">
                Percentage of each SKU&rsquo;s low-stock alert level to hold back from Shopify.
                When both buffers are set, the larger one applies. Leave empty to disable.
              </span>
            </div>
            {settings.safetyBufferPercent != null && settings.safetyBufferPercent > 0 && (
              // This once predicted a mass push to zero and told the operator to
              // raise the guard; a zero-quantity push no longer follows from the
              // buffer that way, so the line states only what the percent buffer
              // actually does. Muted, not amber: it describes normal behaviour,
              // and an alarm colour on a non-alarm is how amber stops meaning
              // anything (DESIGN §1). No shouted word either.
              <p className="pl-6 text-xs text-muted-foreground">
                {t('stockSync.settings.orgFallbackPercent.bothSetNote')}
              </p>
            )}
          </div>

          {/* Mass Out-of-Stock Guard — REORDERED below the safety buffers; helper rewritten */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <Label htmlFor="mass-oos-guard">Mass Out-of-Stock Guard</Label>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <Input
                id="mass-oos-guard"
                type="number"
                inputMode="numeric"
                min={0}
                max={500}
                aria-describedby="mass-oos-guard-help"
                value={settings.zeroStockThreshold}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val >= 0 && val <= 500) update({ zeroStockThreshold: val });
                }}
                className="w-24 tabular-nums"
              />
              <span id="mass-oos-guard-help" className="text-sm text-muted-foreground">
                Halts the push when more than this many SKUs would be sent to Shopify at
                zero in a single cycle. Set to 0 to disable this protection.
              </span>
            </div>
          </div>

          {/* Always 0 limit: its own number, apart from the Mass Out-of-Stock Guard
              above (Always 0 SKUs skip that guard). 0 is stored for No limit. */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Ban className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <Label htmlFor="always-zero-limit">{t('stockSync.settings.alwaysZeroLimit.label')}</Label>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <Input
                id="always-zero-limit"
                type="number"
                inputMode="numeric"
                min={1}
                max={MAX_ALWAYS_ZERO_LIMIT}
                disabled={alwaysZeroNoLimit}
                aria-describedby="always-zero-limit-help"
                value={alwaysZeroNoLimit ? '' : settings.alwaysZeroLimit}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val >= 1 && val <= MAX_ALWAYS_ZERO_LIMIT) update({ alwaysZeroLimit: val });
                }}
                className="w-28 tabular-nums"
              />
              <div className="flex items-center gap-2">
                <Checkbox
                  id="always-zero-no-limit"
                  checked={alwaysZeroNoLimit}
                  onCheckedChange={(checked) =>
                    update({ alwaysZeroLimit: checked === true ? 0 : DEFAULT_ALWAYS_ZERO_LIMIT })
                  }
                />
                <Label htmlFor="always-zero-no-limit" className="font-normal">
                  {t('stockSync.settings.alwaysZeroLimit.noLimit')}
                </Label>
              </div>
              <span id="always-zero-limit-help" className="text-sm text-muted-foreground">
                {t('stockSync.settings.alwaysZeroLimit.help')}
              </span>
            </div>
          </div>
        </CardContent>
        </CollapsibleContent>
      </Card>
      </Collapsible>

      {/* Approval Queue Settings */}
      <Collapsible open={openSections.has('approval')} onOpenChange={() => toggleSection('approval')}>
      <Card>
        <CollapsibleCardHeader
          open={openSections.has('approval')}
          icon={
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <ClipboardCheck className="h-5 w-5 text-amber-600 dark:text-amber-400" aria-hidden="true" />
            </div>
          }
          title="Approval Queue"
          description="Hold significant stock changes for admin review before pushing to Shopify."
        />
        <CollapsibleContent>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="approval-enabled">Enable Approval Queue</Label>
              <p className="text-sm text-muted-foreground">Require admin approval for stock changes that exceed thresholds.</p>
            </div>
            <Switch id="approval-enabled" checked={settings.approvalEnabled} onCheckedChange={(checked) => update({ approvalEnabled: checked })} />
          </div>

          {settings.approvalEnabled && (
            <>
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="zero-protect">Zero-Out Protection</Label>
                  <p className="text-sm text-muted-foreground">{t('stockSync.approval.zeroOutHelp')}</p>
                </div>
                <Switch id="zero-protect" checked={settings.zeroOutProtection} onCheckedChange={(checked) => update({ zeroOutProtection: checked })} />
              </div>

              <div className="space-y-2">
                <Label>Drop Units Threshold</Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    min={1}
                    max={10000}
                    value={settings.dropUnitsThreshold}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val) && val >= 1 && val <= 10000) update({ dropUnitsThreshold: val });
                    }}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">require approval when a SKU drops by more than this many units</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Drop Percent Threshold</Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    value={settings.dropPercentThreshold}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val) && val >= 1 && val <= 100) update({ dropPercentThreshold: val });
                    }}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">%, require approval when a SKU drops by more than this percentage</span>
                </div>
              </div>
            </>
          )}

          {/* BLU-553: Delta block — hard downward-push blocker, independent of
              the threshold rules above; blocked SKUs land in this same queue. */}
          <div className="border-t pt-4 space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <Label htmlFor="delta-block-enabled">Delta Block</Label>
                  {deltaBlockEnabled && (
                    <Badge variant="outline" className="text-green-600 border-green-300">Active</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  Blocks any single push that would drop a SKU by more than this many units vs what
                  was last pushed. Blocked SKUs wait in the approval queue.
                </p>
              </div>
              <Switch
                id="delta-block-enabled"
                checked={deltaBlockEnabled}
                onCheckedChange={(checked) => update({ deltaBlockEnabled: checked } as Partial<InventoryPushSettings>)}
              />
            </div>

            {deltaBlockEnabled && (
              <div className="space-y-2">
                <Label htmlFor="delta-block-threshold">Delta Block Threshold</Label>
                <div className="flex items-center gap-3">
                  <Input
                    id="delta-block-threshold"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    value={deltaBlockThreshold}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val) && val >= 1) {
                        update({ deltaBlockThreshold: val } as Partial<InventoryPushSettings>);
                      }
                    }}
                    className="w-28 tabular-nums"
                  />
                  <span className="text-sm text-muted-foreground">units, pushes with a larger single-SKU drop are held for review</span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
        </CollapsibleContent>
      </Card>
      </Collapsible>

      {/* BLU-343: Push Exclusions. Locations only: SKU rules have their own
          card below, because they apply at once and this card saves with the
          page. */}
      <Collapsible open={openSections.has('exclusions')} onOpenChange={() => toggleSection('exclusions')}>
      <Card>
        <CollapsibleCardHeader
          open={openSections.has('exclusions')}
          icon={
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-orange-100">
              <Filter className="h-5 w-5 text-orange-600" aria-hidden="true" />
            </div>
          }
          title={t('stockSync.pushExclusions.title')}
          description={t('stockSync.pushExclusions.description')}
          aside={
            skippedLocationCount > 0 && (
              <Badge variant="secondary" className="tabular-nums">
                {t('stockSync.pushExclusions.locationsSkipped', { count: skippedLocationCount })}
              </Badge>
            )
          }
        />
        <CollapsibleContent>
        <CardContent className="space-y-3 pt-0">
          <Label className="text-sm font-medium">{t('stockSync.pushExclusions.locationsLabel')}</Label>
          <p className="text-xs text-muted-foreground">{t('stockSync.pushExclusions.locationsHelp')}</p>
          <div className="rounded-md border divide-y">
            {locations.length === 0 ? (
              <p className="px-3 py-4 text-sm text-muted-foreground text-center">{t('stockSync.pushExclusions.noLocations')}</p>
            ) : (
              [...locations].sort((a, b) => a.name.localeCompare(b.name)).map(location => {
                const skipped = excludedLocationSet.has(location.id);
                return (
                  <div key={location.id} className="flex items-center justify-between px-3 py-2">
                    <span className="text-sm">{location.name}</span>
                    <div className="flex items-center gap-2">
                      {/* Neutral words plus a glyph: skipping a location is a
                          choice, not a fault, so it is not coloured as one. */}
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 text-xs font-medium',
                          skipped ? 'text-foreground' : 'text-muted-foreground',
                        )}
                      >
                        {skipped && <PauseCircle className="h-3 w-3" aria-hidden="true" />}
                        {skipped ? t('stockSync.pushExclusions.skipped') : t('stockSync.pushExclusions.pushing')}
                      </span>
                      <Switch
                        checked={!skipped}
                        onCheckedChange={() => toggleLocationExclusion(location.id)}
                        aria-label={t('stockSync.pushExclusions.pushTo', { location: location.name })}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <p className="text-xs text-muted-foreground tabular-nums">
            {t('stockSync.pushExclusions.locationsSkipped', { count: skippedLocationCount })}
          </p>
        </CardContent>
        </CollapsibleContent>
      </Card>
      </Collapsible>

      {/* BLU-1541: Skip or Always 0, per SKU. Applies at once, like the ERP
          stock authority card after it, so it sits beside it and outside
          this form's save (every control in it is type="button"). The header
          badges read the same capped list the card shows (shared query
          cache), so a closed card still says SKUs are being zeroed. */}
      <Collapsible open={openSections.has('skuRules')} onOpenChange={() => toggleSection('skuRules')}>
      <Card>
        <CollapsibleCardHeader
          open={openSections.has('skuRules')}
          icon={
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-100">
              <Ban className="h-5 w-5 text-amber-700" aria-hidden="true" />
            </div>
          }
          title={t('stockSync.pushRules.card.title')}
          description={t('stockSync.pushRules.card.description')}
          aside={
            ruleCounts && (ruleCounts.skip > 0 || ruleCounts.alwaysZero > 0) && (
              <div className="flex flex-wrap items-center gap-1.5">
                {ruleCounts.skip > 0 && (
                  <Badge variant="outline" className="gap-1 tabular-nums text-foreground">
                    <PauseCircle className="h-3 w-3" aria-hidden="true" />
                    {t('stockSync.pushRules.card.skipped', { count: ruleCounts.skip })}
                  </Badge>
                )}
                {ruleCounts.alwaysZero > 0 && (
                  <Badge variant="outline" className={cn('gap-1 tabular-nums', ALWAYS_ZERO_BADGE_CLASS)}>
                    <Ban className="h-3 w-3" aria-hidden="true" />
                    {t('stockSync.pushRules.card.alwaysZero', { count: ruleCounts.alwaysZero })}
                  </Badge>
                )}
              </div>
            )
          }
        >
          <NoteLine icon={Zap}>{t('stockSync.pushRules.card.appliesNow')}</NoteLine>
        </CollapsibleCardHeader>
        <CollapsibleContent>
        <CardContent className="pt-0">
          <PushSkuRulesSection
            runState={runState}
            alwaysZeroLimit={savedPush.alwaysZeroLimit}
            raiseSelectionBar={hasChanges}
          />
        </CardContent>
        </CollapsibleContent>
      </Card>
      </Collapsible>

      {/* BLU-715b: per-location ERP stock authority — the second key the
          reconcile's zeroing pass needs alongside the org-wide
          `zeroUnstockedLocations` flag. Sits next to Push Exclusions because
          both answer "what does this location mean to a Shopify write", and it
          is NOT part of this form's dirty model: each switch applies on toggle
          (the section says so in its own copy). Its own controls are Radix
          buttons / explicit type="button", so nothing here can implicit-submit
          the org-settings form around it. */}
      <ErpStockAuthoritySection />

      {/* POD Variant Discovery */}
      <Collapsible open={openSections.has('discovery')} onOpenChange={() => toggleSection('discovery')}>
      <Card>
        <CollapsibleCardHeader
          open={openSections.has('discovery')}
          icon={
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
              <Search className="h-5 w-5 text-blue-600 dark:text-blue-400" aria-hidden="true" />
            </div>
          }
          title="Variant Pre-Discovery"
          description="Speed up pushes by pre-loading product variant data from Shopify. Schedule this for off-peak hours."
        />
        <CollapsibleContent>
        <CardContent className="space-y-6">
          {/* First Sync Audit — pre-flight report before an org's first real inventory push */}
          <div
            className={cn(
              'rounded-lg border p-4 space-y-4',
              settings.enabled && auditResult?.firstSync
                ? 'border-amber-300 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20'
                : 'border-muted bg-muted/20',
            )}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="space-y-0.5">
                <Label>First Sync Audit</Label>
                <p className="text-sm text-muted-foreground">
                  Preview what would push, what&rsquo;s already cache-warm, and a suggested discovery pace before enabling live push.
                </p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={runFirstPushAudit} disabled={isAuditing}>
                {isAuditing ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />Auditing...</>
                ) : (
                  'Run audit'
                )}
              </Button>
            </div>

            {auditResult && (
              <div className="space-y-3">
                {auditResult.firstSync && (
                  <Badge variant="outline" className="text-amber-700 border-amber-300">First sync detected: no prior pushes recorded</Badge>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Would push</p>
                    <p className="font-medium">{auditResult.totalChanges} SKU(s)</p>
                    <p className="text-xs text-muted-foreground">{auditResult.papChanges} PAP · {auditResult.podChanges} POD</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Cached vs needs discovery</p>
                    <p className="font-medium">{auditResult.cacheWarmCount} cached / {auditResult.needsDiscoveryCount} to discover</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Est. variants</p>
                    <p className="font-medium">
                      {auditResult.estimatedVariantsTotal != null
                        ? `~${auditResult.estimatedVariantsTotal} (avg ${auditResult.avgVariantsPerBlank}/blank)`
                        : 'Not available for custom catalogs'}
                    </p>
                    {auditResult.variantEstimateSampled && (
                      <p className="text-xs text-muted-foreground">Estimated from a sample</p>
                    )}
                  </div>
                  <div>
                    <p className="text-muted-foreground">Ramp time at current cap</p>
                    <p className="font-medium">
                      {auditResult.estimatedCyclesAtCurrentCap == null
                        ? '-'
                        : auditResult.needsDiscoveryCount > 0
                          ? `~${auditResult.estimatedCyclesAtCurrentCap} cycle(s) (~${auditResult.estimatedMinutesAtCurrentCap} min)`
                          : 'No ramp needed'}
                    </p>
                  </div>
                </div>

                {auditResult.suggestedCap != null && auditResult.suggestedCap !== auditResult.currentCap && (
                  <div className="flex items-center justify-between gap-3 rounded-md border border-primary/30 bg-primary/5 p-3">
                    <p className="text-sm">
                      Suggested cap: <span className="font-medium">{auditResult.suggestedCap}/cycle</span> (currently {auditResult.currentCap}/cycle)
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => update({ discoveriesPerCycle: auditResult.suggestedCap })}
                    >
                      Apply
                    </Button>
                  </div>
                )}

                {auditResult.recommendPreDiscovery && (
                  <p className="text-xs text-muted-foreground">
                    Large discovery backlog. Consider the <span className="font-medium">Discover Now</span> button below (or scheduling a nightly run) to warm the cache instead of grinding through cycles.
                  </p>
                )}

                {auditResult.goingToZeroCount > 0 && (
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    {auditResult.goingToZeroCount} SKU(s) would drop to zero stock on Shopify, review before enabling live push.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <Label>Nightly Schedule</Label>
            </div>
            <div className="flex items-center gap-3">
              <Select
                value={settings.discoveryHourUtc != null ? String(settings.discoveryHourUtc) : 'disabled'}
                onValueChange={(value) => update({ discoveryHourUtc: value === 'disabled' ? null : Number(value) })}
              >
                <SelectTrigger className="w-56">
                  <SelectValue placeholder="Disabled" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="disabled">Disabled</SelectItem>
                  {Array.from({ length: 24 }, (_, utcHour) => {
                    // Build a Date at this UTC hour to derive the user's local time + timezone abbreviation
                    const date = new Date();
                    date.setUTCHours(utcHour, 0, 0, 0);
                    const localTime = date.toLocaleTimeString('en-AU', {
                      hour: 'numeric',
                      minute: '2-digit',
                      timeZone: 'Australia/Sydney',
                      timeZoneName: 'short',
                    });
                    const utcDisplay = String(utcHour).padStart(2, '0');
                    return (
                      <SelectItem key={utcHour} value={String(utcHour)}>
                        {localTime} ({utcDisplay}:00 UTC)
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">
                nightly schedule for variant discovery
              </span>
            </div>
          </div>

          {/* Discoveries Per Cycle — exposes the previously env-only MAX_NEW_DISCOVERIES_PER_CYCLE */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <Label htmlFor="discoveries-per-cycle">Discoveries Per Cycle</Label>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <div className="relative w-28">
                <Input
                  id="discoveries-per-cycle"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={25}
                  placeholder="5"
                  aria-describedby="discoveries-per-cycle-help"
                  value={settings.discoveriesPerCycle ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      update({ discoveriesPerCycle: null });
                      return;
                    }
                    const val = parseInt(raw, 10);
                    if (!isNaN(val) && val >= 1 && val <= 25) {
                      update({ discoveriesPerCycle: val });
                    }
                  }}
                  className="tabular-nums"
                />
              </div>
              <span id="discoveries-per-cycle-help" className="text-sm text-muted-foreground">
                New SKUs discovered per push cycle. Higher ramps faster but uses more Shopify API budget.
              </span>
            </div>
          </div>

          {/* Auto-Enable Tracking Toggle */}
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <Label htmlFor="auto-track">Auto-Enable Tracking</Label>
                {settings.autoEnableTracking && (
                  <Badge variant="outline" className="text-green-600 border-green-300">Active</Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                During nightly discovery, enable inventory tracking on newly found on-demand variants
                and push current stock immediately. Pre-enables tracking so push cycles run faster.
                Leave off to review variants manually first.
              </p>
            </div>
            <Switch
              id="auto-track"
              checked={settings.autoEnableTracking}
              onCheckedChange={(checked) => update({ autoEnableTracking: checked })}
            />
          </div>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => discoveryMutation.mutate()}
              disabled={discoveryMutation.isPending || isRunning}
            >
              {isRunning ? (
                <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />Running...</>
              ) : (
                <><Search className="h-3.5 w-3.5 mr-1.5" />Discover Now</>
              )}
            </Button>
            <span className="text-xs text-muted-foreground">
              {t('stockSync.settings.discoverNow.help')}
            </span>
          </div>

          {/* Discovery Status */}
          {showStatus && discoveryStatus && (
            <>
              {/* Running */}
              {isRunning && (
                <div role="status" className="rounded-lg border border-blue-200 bg-blue-50/50 p-3 space-y-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    <span className="text-sm font-medium text-blue-900">
                      Processing SKU {discoveryStatus.processedSkus + 1} of {discoveryStatus.totalSkus || '...'}
                    </span>
                  </div>
                  <Progress
                    value={progressPercent}
                    className="h-2 [&>div]:bg-blue-500"
                  />
                  {discoveryStatus.currentSku && (
                    <p className="font-mono text-xs text-blue-700">{discoveryStatus.currentSku}</p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {discoveryStatus.cached > 0 && (
                      <Badge variant="secondary" className="text-xs">{discoveryStatus.cached} cached</Badge>
                    )}
                    {discoveryStatus.skipped > 0 && (
                      <Badge variant="secondary" className="text-xs">{discoveryStatus.skipped} skipped</Badge>
                    )}
                    {discoveryStatus.trackingEnabled > 0 && (
                      <Badge variant="secondary" className="text-xs">{discoveryStatus.trackingEnabled} tracking enabled</Badge>
                    )}
                    {discoveryStatus.quantitiesPushed > 0 && (
                      <Badge variant="secondary" className="text-xs">{discoveryStatus.quantitiesPushed} quantities pushed</Badge>
                    )}
                    {discoveryStatus.errors > 0 && (
                      <Badge variant="destructive" className="text-xs">{discoveryStatus.errors} errors</Badge>
                    )}
                  </div>
                </div>
              )}

              {/* Complete */}
              {isComplete && (
                <div role="status" className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-emerald-900">Discovery complete</p>
                        <div className="flex flex-wrap gap-2">
                          {discoveryStatus.cached > 0 && (
                            <Badge variant="secondary" className="text-xs">{discoveryStatus.cached} cached</Badge>
                          )}
                          {discoveryStatus.skipped > 0 && (
                            <Badge variant="secondary" className="text-xs">{discoveryStatus.skipped} already cached</Badge>
                          )}
                          {discoveryStatus.partial > 0 && (
                            <Badge variant="secondary" className="text-xs">{discoveryStatus.partial} partial</Badge>
                          )}
                          {discoveryStatus.trackingEnabled > 0 && (
                            <Badge variant="secondary" className="text-xs">{discoveryStatus.trackingEnabled} tracking enabled</Badge>
                          )}
                          {discoveryStatus.quantitiesPushed > 0 && (
                            <Badge variant="secondary" className="text-xs">{discoveryStatus.quantitiesPushed} quantities pushed</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      aria-label="Dismiss"
                      onClick={() => setDismissedStatus(true)}
                      className="text-emerald-500 hover:text-emerald-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Error */}
              {isError && (
                <div role="alert" className="rounded-lg border border-red-200 bg-red-50/50 p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-red-900">Discovery completed with errors</p>
                        <div className="flex flex-wrap gap-2">
                          {discoveryStatus.errors > 0 && (
                            <Badge variant="destructive" className="text-xs">{discoveryStatus.errors} errors</Badge>
                          )}
                          {discoveryStatus.cached > 0 && (
                            <Badge variant="secondary" className="text-xs">{discoveryStatus.cached} cached</Badge>
                          )}
                          {discoveryStatus.skipped > 0 && (
                            <Badge variant="secondary" className="text-xs">{discoveryStatus.skipped} skipped</Badge>
                          )}
                        </div>
                        <p className="text-xs text-red-700">Check backend logs for details</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      aria-label="Dismiss"
                      onClick={() => setDismissedStatus(true)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
        </CollapsibleContent>
      </Card>
      </Collapsible>

      {/* BLU-495 Q4: Availability Back-Sync card removed. Threshold-driven
          unsellability is now handled by `safetyBufferPercent` on the push
          card above, which pushes qty=0 to Shopify when stock crosses the
          per-SKU buffer. See .planning/phases/55-blu-495-q4-deprecate-back-sync/PLAN.md */}

      {/* Notifications moved to /notifications. Keep a pointer card so
          operators looking for the Slack toggle here aren't lost. */}
      <Card>
        <CardHeader className="cursor-pointer select-none" onClick={() => window.location.assign('/notifications')}>
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-900/30">
              <svg viewBox="0 0 24 24" className="h-5 w-5 text-violet-600 dark:text-violet-400" fill="currentColor" aria-hidden="true">
                <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.124 2.521a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.52 2.521h-2.522V8.834zm-1.268 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.167 0a2.528 2.528 0 0 1 2.523 2.522v6.312zm-2.523 10.124a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.167 24a2.527 2.527 0 0 1-2.52-2.52v-2.523h2.52zm0-1.268a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.52 2.523h-6.313z" />
              </svg>
            </div>
            <div className="flex-1">
              <CardTitle className="text-base">Slack Notifications</CardTitle>
              <CardDescription>
                Moved to <Link to="/notifications" className="text-threadheads-purple hover:underline">Notifications</Link>, which manages PO digests + stock-sync alerts in one place.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Save bar */}
      {hasChanges && (
        <div className="sticky bottom-4 z-10 flex justify-center">
          <div className="inline-flex items-center gap-4 rounded-lg bg-primary px-5 py-3 text-primary-foreground shadow-lg">
            <p className="text-sm text-primary-foreground/80">Unsaved changes</p>
            <div className="flex gap-2">
              <Button type="button" variant="secondary" size="sm" onClick={handleReset} disabled={updateOrganization.isPending}>
                Cancel
              </Button>
              <Button type="submit" variant="secondary" size="sm" disabled={updateOrganization.isPending}>
                {updateOrganization.isPending
                  ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Saving...</>
                  : <><Save className="mr-1.5 h-3.5 w-3.5" />Save Changes</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
